import React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Chip,
  Typography,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount, useBalance, useDisconnect, useChainId } from 'wagmi';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LogoutIcon from '@mui/icons-material/Logout';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import sebearLogo from '../assets/images/Sebear-logo.svg';
import sebearIsotype from '../assets/images/Sebear-Isotype-256px.svg';
import ThemeToggle from './ThemeToggle';
import { useThemeMode } from '../contexts/ThemeContext';

// Chain name mapping for known chains
const getChainName = (chainId: number | undefined): string => {
  const chainNames: Record<number, string> = {
    1: 'Ethereum',
    11155111: 'Sepolia',
    17000: 'Hoodi',
    137: 'Polygon',
    80002: 'Polygon Amoy',
    56: 'BSC',
    97: 'BSC Testnet',
    42161: 'Arbitrum',
    421614: 'Arbitrum Sepolia',
    10: 'Optimism',
    11155420: 'OP Sepolia',
    8453: 'Base',
    84532: 'Base Sepolia',
  };
  return chainNames[chainId || 0] || `Chain ${chainId}`;
};

// Truncate wallet address for display
const truncateAddress = (address: string | undefined): string => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { disconnect } = useDisconnect();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const textPrimary = isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.9)';
  const textSecondary = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(15, 23, 42, 0.65)';
  const textMuted = isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(15, 23, 42, 0.5)';
  const navInactive = isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(15, 23, 42, 0.75)';
  const navDivider = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(15, 23, 42, 0.15)';
  const logoFilter = isDark
    ? 'none'
    : 'invert(37%) sepia(93%) saturate(1352%) hue-rotate(196deg) brightness(115%) contrast(105%) drop-shadow(0 1px 2px rgba(15, 23, 42, 0.25))';
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [addressCopied, setAddressCopied] = React.useState(false);

  // Wagmi hooks for real wallet data
  const { address, isConnected, isConnecting } = useAccount();
  const chainId = useChainId();
  const { data: balanceData, isLoading: balanceLoading } = useBalance({
    address: address,
  });

  // Format balance for display
  const formattedBalance = React.useMemo(() => {
    if (!balanceData) return '0.00';
    const value = parseFloat(balanceData.formatted);
    return value.toFixed(4);
  }, [balanceData]);

  const networkName = getChainName(chainId);

  // Copy address to clipboard
  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2000);
    }
  };

  // Role-based navigation structure
  const getNavItems = () => {
    const baseItems = [
      { label: 'Profile', path: '/profile', icon: <PersonIcon />, tooltip: 'View your profile' },
    ];

    if (!user) return baseItems;

    const roleSpecificItems = [];

    if (user.role === 'issuer') {
      roleSpecificItems.push(
        { label: 'Submit', path: '/submit', icon: <AddIcon />, tooltip: 'Submit a new project' },
        { label: 'My Projects', path: '/dashboard/issuer', icon: <BusinessIcon />, tooltip: 'Manage your projects' }
      );
    } else if (user.role === 'investor') {
      roleSpecificItems.push(
        { label: 'Browse', path: '/dashboard/investor', icon: <AccountBalanceWalletIcon />, tooltip: 'Browse available projects' },
        { label: 'My Investments', path: '/dashboard/investor', icon: <DashboardIcon />, tooltip: 'View your investments' }
      );
    } else if (user.role === 'admin') {
      roleSpecificItems.push(
        { label: 'Admin', path: '/admin', icon: <AdminPanelSettingsIcon />, tooltip: 'Admin panel' },
        { label: 'All Projects', path: '/dashboard/investor', icon: <BusinessIcon />, tooltip: 'View all projects' }
      );
    }

    return [...baseItems, ...roleSpecificItems];
  };

  const navItems = getNavItems();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    disconnect();
    handleUserMenuClose();
    navigate('/login');
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return { bg: 'linear-gradient(135deg, #F59E0B, #D97706)', color: '#FFF' };
      case 'issuer':
        return { bg: 'linear-gradient(135deg, #10B981, #059669)', color: '#FFF' };
      default:
        return { bg: 'linear-gradient(135deg, #3B82F6, #2563EB)', color: '#FFF' };
    }
  };

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <img
          src={sebearIsotype}
          alt="Sebear RWA"
          style={{ height: '44px', width: 'auto', filter: logoFilter }}
          onClick={handleDrawerToggle}
        />
        <ThemeToggle size="small" />
      </Box>
      {user && (
        <Box sx={{ mb: 2, px: 2 }}>
          <Chip
            label={user.role.toUpperCase()}
            size="small"
            sx={{
              background: getRoleBadgeColor(user.role).bg,
              color: getRoleBadgeColor(user.role).color,
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
        </Box>
      )}
      <List>
        {navItems.map((item, index) => (
          <React.Fragment key={item.label}>
            <ListItem onClick={() => handleNavigation(item.path)} sx={{ cursor: 'pointer' }}>
              <ListItemIcon sx={{ color: location.pathname === item.path ? '#3B82F6' : 'inherit', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  color: location.pathname === item.path ? '#3B82F6' : 'text.primary',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItem>
            {index === 0 && user && navItems.length > 1 && <Divider sx={{ my: 1, opacity: 0.3 }} />}
          </React.Fragment>
        ))}

        {user && (
          <>
            <Divider sx={{ my: 2, opacity: 0.3 }} />

            {/* Wallet Address */}
            <ListItem
              onClick={handleCopyAddress}
              sx={{
                cursor: 'pointer',
                '&:hover': { background: 'rgba(59, 130, 246, 0.1)' }
              }}
            >
              <ListItemIcon>
                <AccountBalanceWalletIcon sx={{ color: '#3B82F6', minWidth: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: textPrimary, fontFamily: 'monospace' }}>
                      {truncateAddress(address)}
                    </Typography>
                    {addressCopied ? (
                      <CheckIcon sx={{ fontSize: 14, color: '#10B981' }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 14, color: textMuted }} />
                    )}
                  </Box>
                }
                secondary="Tap to copy"
              />
            </ListItem>

            {/* Network Status */}
            <ListItem>
              <ListItemIcon>
                <Box sx={{ minWidth: 40, display: 'flex', alignItems: 'center' }}>
                  <FiberManualRecordIcon sx={{ color: '#10B981', fontSize: 12, mr: 0.5 }} />
                  <NetworkCheckIcon sx={{ color: textMuted, fontSize: 20 }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={networkName}
                secondary="Connected"
                sx={{
                  '& .MuiListItemText-primary': { color: textPrimary },
                  '& .MuiListItemText-secondary': { color: '#10B981', fontSize: '0.75rem' }
                }}
              />
            </ListItem>

            {/* Balance */}
            <ListItem>
              <ListItemIcon>
              <AccountBalanceIcon sx={{ color: textMuted, minWidth: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  balanceLoading ? (
                  <Skeleton width={80} height={20} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                  ) : (
                    `${formattedBalance} ${balanceData?.symbol || 'ETH'}`
                  )
                }
                secondary="Balance"
                sx={{
                '& .MuiListItemText-primary': { color: textPrimary, fontWeight: 600 }
                }}
              />
            </ListItem>

            <Divider sx={{ my: 1, opacity: 0.3 }} />

            <ListItem onClick={handleLogout} sx={{ cursor: 'pointer' }}>
              <ListItemIcon>
                <LogoutIcon sx={{ color: '#EF4444', minWidth: 40 }} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{ color: '#EF4444' }}
              />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  const renderNavButton = (item: { label: string; path: string; icon: React.ReactNode; tooltip: string }, isActive: boolean) => (
    <Tooltip title={item.tooltip} arrow placement="bottom" key={item.label}>
      <Button
        color="inherit"
        onClick={() => handleNavigation(item.path)}
        startIcon={item.icon}
        sx={{
          position: 'relative',
          color: isActive ? '#3B82F6' : navInactive,
          fontWeight: isActive ? 600 : 400,
          fontSize: '0.875rem',
          px: 2,
          py: 1,
          borderRadius: 2,
          background: isActive
            ? 'rgba(59, 130, 246, 0.1)'
            : 'transparent',
          border: isActive
            ? '1px solid rgba(59, 130, 246, 0.3)'
            : '1px solid transparent',
          '&:hover': {
            color: '#3B82F6',
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          },
          transition: 'all 0.2s ease',
          minWidth: 'auto',
        }}
      >
        {item.label}
      </Button>
    </Tooltip>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: isDark ? 'rgba(11, 17, 32, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 0.5 }}>
            <Box
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => navigate('/')}
            >
              <img
                src={isMobile ? sebearIsotype : sebearLogo}
                alt="Sebear"
                style={{ height: isMobile ? '36px' : '44px', width: 'auto', filter: logoFilter }}
              />
            </Box>

            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#3B82F6',
                  '&:hover': {
                    color: '#60A5FA',
                    background: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Base navigation items */}
                {navItems.slice(0, 1).map((item) => {
                  const isActive = location.pathname === item.path;
                  return renderNavButton(item, isActive);
                })}

                {/* Role-specific navigation with visual separator */}
                {user && navItems.length > 1 && (
                  <>
                    <Box sx={{ width: '1px', height: '20px', background: navDivider, mx: 1 }} />
                    {navItems.slice(1).map((item) => {
                      const isActive = location.pathname === item.path;
                      return renderNavButton(item, isActive);
                    })}
                  </>
                )}

                <Box sx={{ width: '1px', height: '20px', background: navDivider, mx: 1 }} />

                <ThemeToggle size="small" />

                {user && isConnected ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {/* Network & Balance Indicator */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 0.75,
                        borderRadius: 2,
                        background: 'rgba(16, 185, 129, 0.08)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FiberManualRecordIcon sx={{ color: '#10B981', fontSize: 8 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: textSecondary,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                          }}
                        >
                          {networkName}
                        </Typography>
                      </Box>
                      <Box sx={{ width: '1px', height: '14px', background: navDivider }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: textPrimary,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: 'monospace',
                        }}
                      >
                        {balanceLoading ? '...' : `${formattedBalance} ${balanceData?.symbol || 'ETH'}`}
                      </Typography>
                    </Box>

                    {/* User Account Button */}
                    <Tooltip title="Account settings" arrow placement="bottom">
                      <Button
                        color="inherit"
                        onClick={handleUserMenuOpen}
                        sx={{
                          color: navInactive,
                          fontWeight: 500,
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)',
                          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.12)'}`,
                          '&:hover': {
                            background: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.08)',
                            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.18)'}`,
                          },
                          minWidth: 'auto',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: textPrimary,
                              fontSize: '0.8rem',
                            }}
                          >
                            {truncateAddress(address)}
                          </Typography>
                          <Chip
                            label={user.role}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              background: getRoleBadgeColor(user.role).bg,
                              color: getRoleBadgeColor(user.role).color,
                            }}
                          />
                        </Box>
                      </Button>
                    </Tooltip>
                  </Box>
                ) : isConnecting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#3B82F6' }} />
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                      Connecting...
                    </Typography>
                  </Box>
                ) : (
                  <ConnectButton />
                )}

                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                      borderRadius: 2,
                      mt: 1,
                      minWidth: 200,
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem onClick={() => { handleNavigation('/profile'); handleUserMenuClose(); }}>
                    <ListItemIcon>
                      <PersonIcon sx={{ color: '#3B82F6' }} />
                    </ListItemIcon>
                    Profile
                  </MenuItem>

                  <Divider sx={{ my: 1, opacity: 0.3 }} />

                  {/* Wallet Address - Clickable to copy */}
                  <MenuItem onClick={handleCopyAddress} sx={{ '&:hover': { background: 'rgba(59, 130, 246, 0.1)' } }}>
                    <ListItemIcon>
                      <AccountBalanceWalletIcon sx={{ color: '#3B82F6' }} />
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">Wallet</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.primary" sx={{ fontFamily: 'monospace' }}>
                          {truncateAddress(address)}
                        </Typography>
                        {addressCopied ? (
                          <CheckIcon sx={{ fontSize: 14, color: '#10B981' }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: 14, color: textMuted }} />
                        )}
                      </Box>
                    </Box>
                  </MenuItem>

                  {/* Network Status */}
                  <MenuItem disabled>
                    <ListItemIcon>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FiberManualRecordIcon sx={{ color: '#10B981', fontSize: 10, mr: 0.5 }} />
                        <NetworkCheckIcon sx={{ color: textMuted }} />
                      </Box>
                    </ListItemIcon>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Network</Typography>
                      <Typography variant="caption" color="text.primary">{networkName}</Typography>
                    </Box>
                  </MenuItem>

                  {/* Balance */}
                  <MenuItem disabled>
                    <ListItemIcon>
                      <AccountBalanceIcon sx={{ color: textMuted }} />
                    </ListItemIcon>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Balance</Typography>
                      {balanceLoading ? (
                        <Skeleton width={60} height={16} sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }} />
                      ) : (
                        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                          {formattedBalance} {balanceData?.symbol || 'ETH'}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>

                  <Divider sx={{ my: 1, opacity: 0.3 }} />

                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon sx={{ color: '#EF4444' }} />
                    </ListItemIcon>
                    <Typography color="#EF4444">Logout</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 260,
            background: isDark ? 'rgba(11, 17, 32, 0.98)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRight: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
